import React, { useMemo, useState, useEffect } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { ChartCard, Chip, M3Button } from "../../../components/m3";
import { getOrgTokens } from "../../../theme/style/organizationPalette";
import { fetchWithToken } from "../../../authentication/AuthProvider";
import config from "../../../config";

const FALLBACK_RECOMMENDATIONS = [
  {
    title: "Increase recycled content in metals",
    impact: "+6.2 MCI pts",
    effort: "Medium",
    detail: "Transition high-mass metallic items from virgin feedstock to ≥60% recycled supply. Estimated carbon reduction: 18%.",
    priority: "High",
  },
  {
    title: "Improve component disassembly",
    impact: "+3.1 MCI pts",
    effort: "Low",
    detail: "Substitute adhesive bonds with standard fasteners to facilitate easier end-of-life material recovery.",
    priority: "High",
  },
  {
    title: "Boost renewable feedstock ratio",
    impact: "+2.4 MCI pts",
    effort: "Low",
    detail: "Increase biobased or organic materials within cores and secondary insulation segments.",
    priority: "Medium",
  },
  {
    title: "Recovery loop agreements",
    impact: "+1.8 MCI pts",
    effort: "Medium",
    detail: "Establish take-back agreements with key suppliers to reach a targeted 90% end-of-life recovery rate.",
    priority: "Medium",
  },
  {
    title: "Substite virgin polymers",
    impact: "+1.2 MCI pts",
    effort: "High",
    detail: "Verify alternative secondary plastic grades for structural and aesthetic components.",
    priority: "Low",
  },
];

const BenchmarkPanel = ({ combined_data }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const mats = useMemo(() => combined_data?.combined_circularities || [], [combined_data]);
  const cacheKey = useMemo(() => `circ_ai_recs_${combined_data?.mass_sum || "default"}`, [combined_data]);

  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState([]);

  // Load cached suggestions from localStorage or generate initial suggestions
  useEffect(() => {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        setRecommendations(JSON.parse(cached));
        return;
      } catch (e) {
        console.warn("Failed to parse cached recommendations", e);
      }
    }
    generateDynamicSuggestions();
  }, [cacheKey, mats]);

  const generateDynamicSuggestions = async () => {
    setLoading(true);
    try {
      // 1. Try to fetch from backend AI insight endpoint
      if (mats.length > 0 && config.apiUrl) {
        const response = await fetchWithToken(`${config.apiUrl}/circularity/ai_circularity_insight`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ materials: mats }),
        });

        if (response.ok) {
          const suggestions = await response.json();
          if (suggestions && suggestions.length > 0) {
            setRecommendations(suggestions);
            localStorage.setItem(cacheKey, JSON.stringify(suggestions));
            setLoading(false);
            return;
          }
        }
      }
    } catch (err) {
      console.warn("AI Backend circularity insight failed, falling back to data-driven generation", err);
    }

    // 2. Local data-driven fallback calculations
    const dynamicItems = [];
    const highVirgin = mats.filter((m) => (m.im_virgin_content || 0) > 40);
    const lowRecovery = mats.filter((m) => (m.eol_recycling_efficiency || 0) < 80);

    highVirgin.slice(0, 2).forEach((m, i) => {
      dynamicItems.push({
        title: `${t("Substitute virgin")} ${m.description}`,
        impact: `+${((m.mass || 0) / (combined_data?.mass_sum || 1) * 10.5).toFixed(1)} MCI pts`,
        effort: "Medium",
        detail: `${t("Replace virgin feedstocks in")} ${m.description} ${t("with circular or secondary content to lower cradle-to-gate carbon intensity by an estimated")} 22%.`,
        priority: "High"
      });
    });

    lowRecovery.slice(0, 2).forEach((m, i) => {
      dynamicItems.push({
        title: `${t("Establish take-back loop for")} ${m.description}`,
        impact: `+${((m.mass || 0) / (combined_data?.mass_sum || 1) * 6.2).toFixed(1)} MCI pts`,
        effort: "Low",
        detail: `${t("Partner with regional distributors to reclaim")} ${m.description} ${t("at decommissioning stages. This raises recovery efficiency to")} 85%.`,
        priority: "Medium"
      });
    });

    // Populate remaining from seed
    while (dynamicItems.length < 4) {
      const fallbackItem = FALLBACK_RECOMMENDATIONS[dynamicItems.length % FALLBACK_RECOMMENDATIONS.length];
      dynamicItems.push(fallbackItem);
    }

    setRecommendations(dynamicItems);
    localStorage.setItem(cacheKey, JSON.stringify(dynamicItems));
    setLoading(false);
  };

  return (
    <ChartCard
      title={t("Recommendations")}
      subtitle={`${t("Generated after MCI calculation")} • ${t("ISO 59020 §7 alignment")}`}
      isEmpty={!loading && recommendations.length === 0}
      emptyTitle={t("No recommendations yet")}
      emptyDescription={t("Run the analysis to generate circularity improvement suggestions.")}
      actions={
        <M3Button
          size="sm"
          variant="filled"
          icon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />}
          loading={loading}
          onClick={generateDynamicSuggestions}
        >
          {t("Re-run analysis")}
        </M3Button>
      }
    >
      <Box sx={{ display: "grid", gap: 3 }}>
        {recommendations.map((r, index) => (
          <Box
            key={index}
            sx={{
              borderBottom: index < recommendations.length - 1 ? `1px solid ${tok.border.divider}` : "none",
              pb: index < recommendations.length - 1 ? 3 : 0,
              textAlign: "left",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              <Chip size="sm" tone="primary">{index + 1}</Chip>
              <Typography sx={{ fontWeight: tok.font.weight.semibold, fontSize: tok.font.size.sectionTitle, color: tok.text.primary, fontFamily: tok.font.family }}>
                {t(r.title)}
              </Typography>
            </Box>
            <Typography sx={{ pl: 5, fontSize: tok.font.size.bodyText, color: tok.text.secondary, fontFamily: tok.font.family, lineHeight: 1.6, textAlign: "left" }}>
              {t(r.detail)}
            </Typography>
          </Box>
        ))}
      </Box>
    </ChartCard>
  );
};

export default BenchmarkPanel;
